// components/secure/DncRegistryPanel.js
// Contact Compliance → Registry subscription & refresh.
//
// The do-not-call refresh had working endpoints (certify, area codes, run now, run
// history) and no screen, so recording the certification or switching an area code
// on meant asking a developer. This is that screen. The registry login itself
// (DNC_CO_ID / password) belongs to the Integrations page and is only reported here.

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { getCurrentUser } from '../../hooks/useCurrentUser';
import DataList from '../../components/shared/DataList';

const input = 'px-3 py-2 border border-gray-300 rounded-md text-sm';
const btn = 'px-3 py-1.5 text-sm bg-slate-800 text-white rounded-md hover:bg-slate-900 disabled:bg-indigo-300';
const ghost = 'px-3 py-1.5 text-sm border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50';
const STATE_TONE = {
    current: 'bg-green-100 text-green-800', ageing: 'bg-amber-100 text-amber-800',
    'never loaded': 'bg-red-100 text-red-800', 'stale — calls blocked': 'bg-red-100 text-red-800',
};
const when = (d) => (d ? new Date(d).toLocaleString() : '—');

export default function DncRegistryPanel({ onChanged }) {
    const api = useApiClient();
    const me = getCurrentUser();
    const broker = !!me?.isManagingBroker;
    const [health, setHealth] = useState(null);
    const [subs, setSubs] = useState([]);
    const [runs, setRuns] = useState([]);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [busy, setBusy] = useState(false);
    const [newCode, setNewCode] = useState({ area_code: '', label: '' });
    const [intervals, setIntervals] = useState(null);
    const [upload, setUpload] = useState({ area_code: '', downloaded_at: '', file: null });
    const [showRuns, setShowRuns] = useState(false);

    const load = useCallback(async () => {
        try {
            const [h, s, r] = await Promise.all([
                api.get('/api/contact-compliance/registry/health'),
                api.get('/api/contact-compliance/registry/subscriptions'),
                api.get('/api/contact-compliance/registry/runs'),
            ]);
            setHealth(h); setSubs(s || []); setRuns(r || []);
            setIntervals({ refresh_interval_days: h.refresh_interval_days, warn_age_days: h.warn_age_days });
        } catch (e) { if (e.status !== 403) setError(e.message); }
    }, [api]);
    useEffect(() => { load(); }, [load]);

    const act = async (fn) => {
        setBusy(true); setError(''); setNotice('');
        try { await fn(); await load(); if (onChanged) onChanged(); }
        catch (e) { setError(e.message); } finally { setBusy(false); }
    };

    const certify = () => {
        if (!window.confirm('Record the certification?\n\nLogging in to the registry asserts that Business Real Estate Corp. accesses it solely to comply with the Telemarketing Sales Rule (16 C.F.R. §310.4(b)(3)(iv)). It is recorded in your name.')) return;
        act(async () => { const r = await api.post('/api/contact-compliance/registry/certify', { agree: true }); setNotice(r.message); });
    };
    const toggle = (s) => {
        if (!s.is_active && !window.confirm(`Mark area code ${s.area_code} as on the subscription?\n\nThe registry bills per area code — only tick codes the brokerage has actually paid for.`)) return;
        act(() => api.put(`/api/contact-compliance/registry/subscriptions/${s.area_code}`, { is_active: !s.is_active }));
    };
    const addCode = () => act(async () => {
        await api.put(`/api/contact-compliance/registry/subscriptions/${newCode.area_code}`, { label: newCode.label, is_active: false });
        setNewCode({ area_code: '', label: '' });
        setNotice('Added (not yet active). Tick it once it is on the paid subscription.');
    });
    const refresh = (dryRun, force) => act(async () => {
        try {
            const r = await api.post('/api/contact-compliance/registry/refresh', { dry_run: dryRun, force });
            setNotice(`${r.outcome}: ${r.message}`);
        } catch (e) {
            // 422 carries the run's own explanation (blocked / failed) — show it as the answer.
            if (e.payload?.message) setError(`${e.payload.outcome || 'failed'}: ${e.payload.message}`);
            else throw e;
        }
    });
    const saveIntervals = () => act(async () => {
        await api.put('/api/contact-compliance/registry/settings', {
            refresh_interval_days: Number(intervals.refresh_interval_days), warn_age_days: Number(intervals.warn_age_days),
        });
        setNotice('Saved.');
    });
    const sendFile = () => act(async () => {
        const fd = new FormData();
        fd.append('area_code', upload.area_code);
        if (upload.downloaded_at) fd.append('downloaded_at', upload.downloaded_at);
        fd.append('file', upload.file);
        const r = await api.post('/api/contact-compliance/registry/upload', fd, true);
        setNotice(r.message);
        setUpload({ area_code: '', downloaded_at: '', file: null });
    });

    if (!health) return error ? <p className="text-sm text-red-600 mb-4">{error}</p> : null;

    return (
        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 space-y-4">
            <div>
                <h2 className="font-semibold text-slate-800">Registry subscription &amp; refresh</h2>
                <p className="text-xs text-slate-500">
                    The scheduled job downloads each active area code every {health.refresh_interval_days} day(s); the legal limit is 31.
                    {health.blocking > 0 && <span className="text-red-700 font-medium"> {health.blocking} active area code(s) are blocking prospecting calls.</span>}
                </p>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            {notice && <p className="text-sm text-green-700">{notice}</p>}

            <div className="grid md:grid-cols-3 gap-3 text-sm">
                <div className={`rounded border p-3 ${health.credentials_configured ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
                    <div className="font-medium">Registry login</div>
                    <div>{health.credentials_configured ? 'Configured' : 'Not configured — add it on the Integrations page, or load files by hand below.'}</div>
                </div>
                <div className={`rounded border p-3 ${health.certification?.agreed ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
                    <div className="font-medium">TSR certification</div>
                    {health.certification?.agreed
                        ? <div>Recorded by {health.certification.by} · {when(health.certification.at)}</div>
                        : <div>Not recorded. The scheduled refresh will not log in until it is.
                            {broker && <button className="ml-2 underline font-medium" disabled={busy} onClick={certify}>Record it</button>}</div>}
                </div>
                <div className="rounded border border-slate-200 p-3">
                    <div className="font-medium">Last run</div>
                    {health.last_run
                        ? <div>{when(health.last_run.started_at)} · <strong>{health.last_run.outcome}</strong><div className="text-xs text-slate-600 mt-0.5">{health.last_run.message}</div></div>
                        : <div>Never run.</div>}
                </div>
            </div>

            {broker && (
                <div className="flex flex-wrap gap-2">
                    <button className={ghost} disabled={busy} onClick={() => refresh(true, false)}>Test the login (dry run)</button>
                    <button className={btn} disabled={busy} onClick={() => refresh(false, false)}>Refresh now</button>
                    <button className={ghost} disabled={busy} onClick={() => refresh(false, true)} title="Download even area codes refreshed recently">Refresh all now</button>
                </div>
            )}

            <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-1">Area codes on the subscription</h3>
                <div className="p-3"><DataList rows={subs} rowKey="area_code" csvName="dnc-area-codes" defaultSort={{ key: 'area_code', dir: 'asc' }}
                    filters={[{ key: 'data', label: 'Data' }]}
                    columns={[
                        { key: 'area_code', label: 'Area code', render: (s) => <span className="font-medium">{s.area_code}</span> },
                        { key: 'label', label: 'Label' },
                        { key: 'is_active', label: 'Paid / active', value: (s) => (s.is_active ? 'yes' : 'no'), search: false,
                            render: (s) => <input type="checkbox" checked={!!s.is_active} disabled={!broker || busy} onChange={() => toggle(s)} /> },
                        { key: 'data', label: 'Data', value: (s) => { const row = health.area_codes.find((a) => a.area_code === s.area_code); return row ? row.state : 'not subscribed'; },
                            render: (s) => {
                                const row = health.area_codes.find((a) => a.area_code === s.area_code);
                                return row ? <span className={`px-2 py-0.5 rounded text-xs ${STATE_TONE[row.state] || ''}`}>{row.state}{row.age_days != null ? ` · ${row.age_days} days` : ''}</span>
                                    : <span className="text-xs text-slate-400">not subscribed</span>;
                            } },
                    ]} /></div>
                {broker && (
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                        <input className={`${input} w-24`} placeholder="Area code" value={newCode.area_code} maxLength={3}
                            onChange={(e) => setNewCode({ ...newCode, area_code: e.target.value.replace(/\D/g, '') })} />
                        <input className={`${input} w-48`} placeholder="Label (e.g. Naperville)" value={newCode.label}
                            onChange={(e) => setNewCode({ ...newCode, label: e.target.value })} />
                        <button className={ghost} disabled={busy || newCode.area_code.length !== 3} onClick={addCode}>Add area code</button>
                    </div>
                )}
            </div>

            {broker && intervals && (
                <div className="flex flex-wrap items-end gap-3 text-sm">
                    <label>Download every (days)
                        <input className={`${input} block w-28 mt-1`} type="number" min="1" max="30" value={intervals.refresh_interval_days}
                            onChange={(e) => setIntervals({ ...intervals, refresh_interval_days: e.target.value })} /></label>
                    <label>Show as ageing after (days)
                        <input className={`${input} block w-28 mt-1`} type="number" min="1" max="30" value={intervals.warn_age_days}
                            onChange={(e) => setIntervals({ ...intervals, warn_age_days: e.target.value })} /></label>
                    <button className={ghost} disabled={busy} onClick={saveIntervals}>Save</button>
                </div>
            )}

            {broker && (
                <div className="border-t border-slate-200 pt-3">
                    <h3 className="text-sm font-semibold text-slate-700">Load a file by hand</h3>
                    <p className="text-xs text-slate-500 mb-2">From telemarketing.donotcall.gov → Download → the area code's full list, flat text format (unzip it first). It replaces what is held for that area code.</p>
                    <div className="flex flex-wrap items-center gap-2">
                        <input className={`${input} w-24`} placeholder="Area code" value={upload.area_code} maxLength={3}
                            onChange={(e) => setUpload({ ...upload, area_code: e.target.value.replace(/\D/g, '') })} />
                        <label className="text-xs text-slate-600">Downloaded on
                            <input className={`${input} ml-1`} type="date" value={upload.downloaded_at}
                                onChange={(e) => setUpload({ ...upload, downloaded_at: e.target.value })} /></label>
                        <input type="file" accept=".txt,.csv,text/plain" onChange={(e) => setUpload({ ...upload, file: e.target.files[0] || null })} />
                        <button className={btn} disabled={busy || upload.area_code.length !== 3 || !upload.file} onClick={sendFile}>Load</button>
                    </div>
                </div>
            )}

            <div>
                <button className="text-sm text-indigo-700 hover:underline" onClick={() => setShowRuns((v) => !v)}>
                    {showRuns ? 'Hide' : 'Show'} refresh history ({runs.length})
                </button>
                {showRuns && (
                    <div className="mt-2 text-xs"><DataList rows={runs} rowKey="run_id" csvName="dnc-refresh-runs" defaultSort={{ key: 'started_at', dir: 'desc' }}
                        filters={[{ key: 'outcome', label: 'Outcome' }]}
                        columns={[
                            { key: 'started_at', label: 'Started', type: 'date', render: (r) => <span className="whitespace-nowrap">{when(r.started_at)}</span> },
                            { key: 'trigger_source', label: 'How' },
                            { key: 'outcome', label: 'Outcome', render: (r) => <span className="font-medium">{r.outcome}</span> },
                            { key: 'numbers_loaded', label: 'Numbers', type: 'number', render: (r) => r.numbers_loaded ?? '' },
                            { key: 'message', label: 'Message' },
                        ]} /></div>
                )}
            </div>
        </div>
    );
}
